import type { DerivedModelsConfig, SearchDefaultsConfig } from "./codegen-types";
import { mapColumn } from "./columns";
import { emitJsonNamespaceBlock, emitJsonRawNamespaceBlock, emitTypeDefsBlock } from "./typed-json";
import type { TypeDefInfo, TypeDefMapping, TypedJsonFieldInfo } from "./typed-json.types";
import type { FKRelationInfo, ResolvedPolymorphic } from "./resolve";
import type { RlsConfig } from "./rls";
import type { EnumInfo, FieldInfo, RelationField, TableInfo, ViewInfo } from "./types";
import { capitalize, emitGeneratedFileBanner, esc, toPascalCase, uncapitalize } from "./utils";

const DB_ATTR_RE = /@db\.(\w+)(?:\((\d+)\))?/;

/**
 * Branding context passed to namespace-emitting functions so they can
 * produce branded ID types for UUID primary-key / foreign-key columns and
 * for user-declared `branded` fields in the model config.
 */
export interface BrandingInfo {
  /** Map of modelName -> { relName -> FKRelationInfo } */
  fkRelations: Record<string, Record<string, FKRelationInfo>>;
  /** Map of modelName -> { fieldName -> true } for custom branded fields */
  brandedFields: Record<string, Record<string, true>>;
  /** All tables for Drizzle FK fallback resolution */
  allTables: TableInfo[];
}

/**
 * Determine whether a JS field name in a TableInfo corresponds to a UUID column.
 */
function isUuidField(t: TableInfo, fieldName: string): boolean {
  const col = (t.table as any)[fieldName];
  if (!col) return false;
  return col.columnType === "PgUUID";
}

/**
 * Determine whether a JS field name in a TableInfo is a primary-key column.
 */
function isPkField(t: TableInfo, fieldName: string): boolean {
  const col = (t.table as any)[fieldName];
  if (!col) return false;
  if (col.primary) return true;
  const sqlName = col.name;
  return t.cfg.primaryKeys.some((pk) =>
    pk.columns.some((c: any) => c.name === sqlName),
  );
}

/**
 * For a FK field, find the target model's export (type) name so we can alias
 * the FK type to <TargetType>.Id.
 */
function findFkTarget(
  t: TableInfo,
  fieldName: string,
  fkRelations: Record<string, Record<string, FKRelationInfo>>,
): string | null {
  const modelFks = fkRelations[t.modelName];
  if (!modelFks) return null;
  for (const fkInfo of Object.values(modelFks)) {
    const idx = fkInfo.fields.indexOf(fieldName);
    if (idx >= 0) {
      return fkInfo.targetExportName;
    }
  }
  return null;
}

function resolveTargetModelName(targetName: string, allTables: TableInfo[]): string {
  const match = allTables.find(
    (table) =>
      table.modelName === targetName ||
      table.exportName === targetName ||
      table.typeName === targetName,
  );
  return match?.modelName ?? uncapitalize(targetName);
}

function getFieldBrand(
  t: TableInfo,
  fieldName: string,
  branding?: BrandingInfo,
): string | null {
  if (!branding) return null;

  if (isUuidField(t, fieldName) && isPkField(t, fieldName)) {
    return `${t.modelName}.${fieldName}`;
  }

  if (branding.brandedFields[t.modelName]?.[fieldName]) {
    return `${t.modelName}.${fieldName}`;
  }

  if (!isUuidField(t, fieldName)) {
    return null;
  }

  let targetName = findFkTarget(t, fieldName, branding.fkRelations);
  if (!targetName) {
    targetName = findFkTargetFromDrizzle(t, fieldName, branding.allTables);
  }

  if (!targetName) {
    return null;
  }

  return `${resolveTargetModelName(targetName, branding.allTables)}.id`;
}

/**
 * Fallback FK target resolution using Drizzle table configs.
 * Checks the table's FK constraints to find which target table a field points to.
 */
function findFkTargetFromDrizzle(
  t: TableInfo,
  fieldName: string,
  allTables: TableInfo[],
): string | null {
  const col = (t.table as any)[fieldName];
  if (!col) return null;
  const sqlName = col.name;

  for (const fk of t.cfg.foreignKeys) {
    const ref = fk.reference();
    const localSqlNames: string[] = ref.columns.map((c: any) => c.name);
    if (!localSqlNames.includes(sqlName)) continue;

    const targetSqlName: string = (ref.foreignTable as any)[Symbol.for("drizzle:Name")] ?? "";
    const targetTable = allTables.find((tt) => tt.sqlName === targetSqlName);
    if (targetTable) return targetTable.exportName;
  }
  return null;
}

function buildDerivedEnumName(modelName: string, fieldName: string) {
  return `${toPascalCase(modelName)}${toPascalCase(fieldName)}`;
}

function inferFieldIsNonNull(defaultWhere: Record<string, unknown> | null, fieldName: string) {
  if (!defaultWhere) return false;
  const condition = defaultWhere[fieldName];
  if (condition === undefined || condition === null) return false;
  if (Array.isArray(condition)) return condition.length > 0;
  if (typeof condition !== "object") return true;
  if (
    Object.prototype.hasOwnProperty.call(condition, "not") &&
    Reflect.get(condition, "not") === null
  ) {
    return true;
  }
  if (Object.prototype.hasOwnProperty.call(condition, "in")) {
    const values = Reflect.get(condition, "in");
    return Array.isArray(values) && values.length > 0;
  }
  return false;
}

function applyDerivedFieldOverrides(field: FieldInfo, table: TableInfo): FieldInfo {
  if (!table.derivedFrom) return field;

  const narrowedValues = table.narrow?.[field.name];

  return {
    ...field,
    // Keep base enum type — narrowing is handled at runtime by derived-models plugin
    optional:
      narrowedValues && narrowedValues.length > 0
        ? false
        : field.optional && !inferFieldIsNonNull(table.defaultWhere, field.name),
  };
}

function collectDerivedEnums(tables: TableInfo[], enumMap: Map<string, EnumInfo>) {
  const derivedEnums = new Map<string, EnumInfo>();
  const tablesByModel = new Map(tables.map((table) => [table.modelName, table]));

  for (const table of tables) {
    if (!table.derivedFrom || !table.narrow) continue;

    const baseTable = tablesByModel.get(table.derivedFrom);
    if (!baseTable) continue;

    const baseFields = new Map<string, FieldInfo>();
    for (const jsKey of baseTable.sqlToJsKey.values()) {
      const column = (baseTable.table as unknown as Record<string, unknown>)[jsKey];
      const field = mapColumn(jsKey, column, enumMap);
      baseFields.set(field.name === "$hash" ? "hash" : field.name, field);
    }

    for (const [fieldName, narrowedValues] of Object.entries(table.narrow)) {
      if (!narrowedValues.length) continue;
      const baseField = baseFields.get(fieldName);
      if (!baseField) {
        throw new Error(`Unknown narrowed field "${table.modelName}.${fieldName}".`);
      }

      const baseEnum = [...enumMap.values()].find((info) => info.zenstackName === baseField.type);
      if (!baseEnum) {
        // Skip narrowing for non-enum fields (e.g. text columns with $type<>())
        continue;
      }

      const values = narrowedValues.map((value) => String(value));
      for (const value of values) {
        if (!baseEnum.values.includes(value)) {
          throw new Error(
            `Invalid narrowed enum value "${value}" for "${table.modelName}.${fieldName}".`,
          );
        }
      }

      const enumName = buildDerivedEnumName(table.modelName, fieldName);
      if (derivedEnums.has(enumName)) continue;

      const mapping = baseEnum.mapping
        ? Object.fromEntries(
            Object.entries(baseEnum.mapping).filter(([, value]) => values.includes(value)),
          )
        : Object.fromEntries(values.map((value) => [toPascalCase(value).toUpperCase(), value]));

      derivedEnums.set(enumName, {
        pgName: `$derived:${enumName}`,
        zenstackName: enumName,
        values,
        mapping,
        mappedName: null,
      });
    }
  }

  return derivedEnums;
}

export function emitEnumBlock(info: EnumInfo): string {
  const lines: string[] = [];
  lines.push(`        ${info.zenstackName}: {`);
  lines.push(`            name: ${esc(info.zenstackName)},`);

  const tuples: (readonly [string, string])[] = info.mapping
    ? (() => {
        const invertedMap = Object.fromEntries(
          Object.entries(info.mapping).map(([k, v]) => [v, k]),
        );
        return info.values.map((v) => {
          const key = invertedMap[v];
          if (!key) throw new Error(`Enum "${info.pgName}" value "${v}" not found in mapping.`);
          return [v, key] as const;
        });
      })()
    : info.values.map((v) => [v, v] as const);
  const valuesEntries = tuples.map(([value]) => `${esc(value)}: ${esc(value)}`);
  lines.push(`            values: { ${valuesEntries.join(", ")} },`);

  lines.push(`            fields: {`);
  for (const [v] of tuples) {
    lines.push(`                ${esc(v)}: { name: ${esc(v)} },`);
  }
  lines.push(`            },`);

  const mappedName = info.mappedName === undefined ? info.pgName : info.mappedName;
  if (mappedName) {
    lines.push(
      `            attributes: [{ name: "@@map", args: [{ name: "name", value: ExpressionUtils.literal(${esc(mappedName)}) }] }]`,
    );
  }
  lines.push(`        }`);
  return lines.join("\n");
}

function emitBrandValue(brand: string | string[]): string {
  if (Array.isArray(brand)) {
    return `[${brand.map((b) => esc(b)).join(", ")}] as const`;
  }
  return esc(brand);
}

export function emitFieldBlock(field: FieldInfo, stripRlsSettings?: Set<string>): string {
  const parts: string[] = [];
  parts.push(`name: ${esc(field.name)}`);
  parts.push(`type: ${esc(field.type)}`);
  if (field.brand) parts.push(`brand: ${emitBrandValue(field.brand)}`);

  if (field.optional) parts.push("optional: true");
  if (field.array) parts.push("array: true");
  if (field.jsonArray) parts.push("jsonArray: true");
  if (field.id) parts.push("id: true");
  if (field.unique) parts.push("unique: true");
  if (field.updatedAt) parts.push("updatedAt: true");

  const attrs: string[] = [];
  if (field.id) attrs.push('{ name: "@id" }');
  if (field.unique) attrs.push('{ name: "@unique" }');
  if (field.createdAt) attrs.push('{ name: "@createdAt" }');
  if (field.updatedAt) attrs.push('{ name: "@updatedAt" }');
  if (field.name !== field.sqlName) {
    attrs.push(
      `{ name: "@map", args: [{ name: "name", value: ExpressionUtils.literal(${esc(field.sqlName)}) }] }`,
    );
  }
  for (const da of field.dbAttrs) {
    const match = da.match(DB_ATTR_RE);
    if (match) {
      const [, attrName, arg] = match;
      if (arg) {
        attrs.push(
          `{ name: "@db.${attrName}", args: [{ name: "x", value: ExpressionUtils.literal(${arg}) }] }`,
        );
      } else {
        attrs.push(`{ name: "@db.${attrName}" }`);
      }
    }
  }
  const isRlsDefault = stripRlsSettings && field.defaultExpr && stripRlsSettings.size > 0 &&
    [...stripRlsSettings].some((setting) => field.defaultExpr!.includes(`current_setting('${setting}')`));
  if (field.defaultExpr && !isRlsDefault) {
    // For array defaults, the attribute value uses ExpressionUtils while the shorthand uses []
    const attrValue =
      field.defaultExpr === "[] as FieldDefault"
        ? 'ExpressionUtils.literal("[]")'
        : field.defaultExpr;
    attrs.push(`{ name: "@default", args: [{ name: "value", value: ${attrValue} }] }`);
  }
  if (field.isGenerated) {
    attrs.push('{ name: "@ignore" }');
  }
  if (field.isJsonColumn && field.type !== "Json") {
    attrs.push('{ name: "@json" }');
  }
  if (attrs.length > 0) {
    parts.push(`attributes: [${attrs.join(", ")}]`);
  }
  if (field.defaultExpr && !isRlsDefault) {
    if (field.defaultExpr.startsWith("ExpressionUtils.literal(")) {
      const inner = field.defaultExpr.slice("ExpressionUtils.literal(".length, -1);
      parts.push(`default: ${inner}`);
    } else {
      parts.push(`default: ${field.defaultExpr}`);
    }
  }

  return `{ ${parts.join(", ")} }`;
}

export function emitRelationField(rel: RelationField, opposite: string): string {
  const parts: string[] = [];
  parts.push(`name: ${esc(rel.fieldName)}`);
  parts.push(`type: ${esc(rel.targetModel)}`);
  if (rel.optional) parts.push("optional: true");
  if (rel.array) parts.push("array: true");

  const relParts: string[] = [];
  if (opposite) relParts.push(`opposite: ${esc(opposite)}`);
  if (rel.relationName) relParts.push(`name: ${esc(rel.relationName)}`);
  if (rel.fields) relParts.push(`fields: [${rel.fields.map((f) => esc(f)).join(", ")}]`);
  if (rel.references)
    relParts.push(`references: [${rel.references.map((r) => esc(r)).join(", ")}]`);
  if (rel.hasDefault) relParts.push("hasDefault: true");
  parts.push(`relation: { ${relParts.join(", ")} }`);

  const attrArgs: string[] = [];
  if (rel.relationName) {
    attrArgs.push(`{ name: "name", value: ExpressionUtils.literal(${esc(rel.relationName)}) }`);
  }
  if (rel.fields) {
    attrArgs.push(
      `{ name: "fields", value: ExpressionUtils.array("String", [${rel.fields.map((f) => `ExpressionUtils.field(${esc(f)})`).join(", ")}]) }`,
    );
  }
  if (rel.references) {
    attrArgs.push(
      `{ name: "references", value: ExpressionUtils.array("String", [${rel.references.map((r) => `ExpressionUtils.field(${esc(r)})`).join(", ")}]) }`,
    );
  }
  if (attrArgs.length > 0) {
    parts.push(`attributes: [{ name: "@relation", args: [${attrArgs.join(", ")}] }]`);
  }

  return `{ ${parts.join(", ")} }`;
}

function emitUnknownLiteral(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return esc(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return `[${value.map((item) => emitUnknownLiteral(item)).join(", ")}]`;
  }
  if (typeof value === "object") {
    return `{ ${Object.entries(value)
      .map(([key, item]) => `${JSON.stringify(key)}: ${emitUnknownLiteral(item)}`)
      .join(", ")} }`;
  }
  throw new Error(`Unsupported config literal: ${String(value)}`);
}

function serializeComputeFunction(compute: (...args: any[]) => any) {
  let computeStr = compute.toString();
  computeStr = computeStr.replace(
    /^(\(?)(\w+)(\)?\s*=>)/,
    (_match, _open, param, rest) =>
      `(${param}: any)${rest.trimStart().startsWith("=>") ? rest.trimStart() : ` ${rest.trimStart()}`}`,
  );
  computeStr = computeStr.replace(/^\(([^)]+)\)\s*=>/, (_, params) => {
    const typed = params
      .split(",")
      .map((param: string) => {
        const trimmed = param.trim();
        return trimmed.includes(":") ? trimmed : `${trimmed}: any`;
      })
      .join(", ");
    return `(${typed}) =>`;
  });
  return computeStr;
}

type DerivedFieldConfig = {
  needs: Record<string, true>;
  compute: (...args: any[]) => any;
};

function getEffectiveDerivedFields(
  tablesByModel: Map<string, TableInfo>,
  derivedFields: Record<string, Record<string, DerivedFieldConfig>>,
  modelName: string,
): Record<string, DerivedFieldConfig> {
  const result: Record<string, DerivedFieldConfig> = {};
  const visited = new Set<string>();

  const visit = (currentModel: string) => {
    if (visited.has(currentModel)) return;
    visited.add(currentModel);

    const table = tablesByModel.get(currentModel);
    if (table?.derivedFrom) {
      visit(table.derivedFrom);
    }

    Object.assign(result, derivedFields[currentModel] ?? {});
  };

  visit(modelName);
  return result;
}

function emitPluginsBlock(
  tables: TableInfo[],
  modelRelations: Map<string, RelationField[]>,
  searchDefaults: SearchDefaultsConfig,
  derivedModels: DerivedModelsConfig,
  modelScopes: Record<string, Record<string, unknown>>,
  derivedFields: Record<string, Record<string, DerivedFieldConfig>>,
): string {
  const tablesByModel = new Map(tables.map((table) => [table.modelName, table]));
  const lines: string[] = [];
  lines.push("    plugins = {");
  lines.push("        virtualRelations: {");

  for (const [model, relations] of modelRelations) {
    const virtualRelations = relations.filter(
      (relation) => relation.kind && relation.kind !== "normal",
    );
    if (virtualRelations.length === 0) continue;

    lines.push(`            ${model}: {`);
    for (const relation of virtualRelations) {
      const parts = [`kind: ${esc(relation.kind!)}`, `targetModel: ${esc(relation.targetModel)}`];
      if (relation.sourceRelation) {
        parts.push(`relation: ${esc(relation.sourceRelation)}`);
      }
      if (relation.throughPath) {
        parts.push(`path: [${relation.throughPath.map((segment) => esc(segment)).join(", ")}]`);
      }
      if (relation.where) {
        parts.push(`where: ${emitUnknownLiteral(relation.where)}`);
      }
      if (relation.single) {
        parts.push("single: true");
      }
      lines.push(`                ${relation.fieldName}: { ${parts.join(", ")} },`);
    }
    lines.push("            },");
  }

  lines.push("        },");
  lines.push("        searchDefaults: {");
  for (const [model, profiles] of Object.entries(searchDefaults)) {
    if (!profiles) continue;

    lines.push(`            ${model}: {`);
    for (const [profile, declaration] of Object.entries(profiles)) {
      const parts = [`fields: ${emitUnknownLiteral(declaration.fields)}`];
      if (declaration.mode) {
        parts.push(`mode: ${esc(declaration.mode)}`);
      }
      if (declaration.strategy) {
        parts.push(`strategy: ${esc(declaration.strategy)}`);
      }
      lines.push(`                ${profile}: { ${parts.join(", ")} },`);
    }
    lines.push("            },");
  }
  lines.push("        },");
  if (Object.keys(derivedModels).length > 0) {
    lines.push("        derivedModels: {");
    for (const [baseModel, derivedMap] of Object.entries(derivedModels)) {
      lines.push(`            ${baseModel}: {`);
      for (const [derivedModel, declaration] of Object.entries(derivedMap)) {
        const parts = [`where: ${emitUnknownLiteral(declaration.where)}`];
        if (declaration.narrow) {
          parts.push(`narrow: ${emitUnknownLiteral(declaration.narrow)}`);
        }
        if (declaration.relations) {
          parts.push(`relations: ${emitUnknownLiteral(declaration.relations)}`);
        }
        lines.push(`                ${derivedModel}: { ${parts.join(", ")} },`);
      }
      lines.push("            },");
    }
    lines.push("        },");
  }
  if (Object.keys(modelScopes).length > 0) {
    lines.push("        modelScopes: {");
    for (const [scopeModel, where] of Object.entries(modelScopes)) {
      lines.push(`            ${scopeModel}: ${emitUnknownLiteral(where)},`);
    }
    lines.push("        },");
  }
  if (Object.keys(derivedFields).length > 0) {
    lines.push("        extResult: {");
    for (const table of tables) {
      const fields = getEffectiveDerivedFields(tablesByModel, derivedFields, table.modelName);
      if (Object.keys(fields).length === 0) continue;
      lines.push(`            ${uncapitalize(table.modelName)}: {`);
      for (const [fieldName, fieldDef] of Object.entries(fields)) {
        lines.push(`                ${fieldName}: {`);
        lines.push(`                    needs: ${emitUnknownLiteral(fieldDef.needs)},`);
        lines.push(`                    compute: ${serializeComputeFunction(fieldDef.compute)},`);
        lines.push("                },");
      }
      lines.push("            },");
    }
    lines.push("        },");
  }
  lines.push("    } as const;");
  return lines.join("\n");
}

export interface EmitSchemaOptions {
  tables: TableInfo[];
  enumMap: Map<string, EnumInfo>;
  modelRelations: Map<string, RelationField[]>;
  inverseRelations: Map<string, RelationField[]>;
  searchDefaults: SearchDefaultsConfig;
  derivedModels?: DerivedModelsConfig;
  modelScopes?: Record<string, Record<string, unknown>>;
  typeDefs?: Map<string, TypeDefInfo>;
  fieldToTypeDef?: Map<string, TypeDefMapping>;
  views?: ViewInfo[];
  derivedFields?: Record<string, Record<string, DerivedFieldConfig>>;
  branding?: BrandingInfo;
  polymorphics?: Record<string, ResolvedPolymorphic>;
  rls?: RlsConfig;
}

function applyTypeDefOverride(
  field: FieldInfo,
  fieldMapping: TypeDefMapping | undefined,
): FieldInfo {
  if (!fieldMapping) return field;

  // `fieldMapping.isArray` reflects the SHAPE OF THE VALUE inside a single jsonb cell
  // (e.g. `col.jsonb("tos").$type<T[]>()` = one cell holding a JS array). It must NOT
  // be lifted to the column's `array` flag for plain jsonb/json columns: ZenStack's
  // `array: true` means "Postgres array column (jsonb[])" and serializes via
  // postgres-array syntax, producing invalid JSON when the column is plain jsonb.
  // Only honour it when the column itself is a native SQL array (`field.array` true).
  const columnIsArray = field.array || (!field.isJsonColumn && fieldMapping.isArray);
  const jsonArray = field.isJsonColumn && fieldMapping.isArray && !columnIsArray;

  return {
    ...field,
    type: fieldMapping.typeDefName,
    array: columnIsArray,
    jsonArray: jsonArray || undefined,
    dbAttrs: field.dbAttrs.filter((attr) => !attr.startsWith("@db.Json")),
  };
}

function emitModelBlock(
  t: TableInfo,
  enumMap: Map<string, EnumInfo>,
  modelRelations: Map<string, RelationField[]>,
  inverseRelations: Map<string, RelationField[]>,
  fieldToTypeDef?: Map<string, TypeDefMapping>,
  branding?: BrandingInfo,
  polymorphics?: Record<string, ResolvedPolymorphic>,
  stripRlsSettings?: Set<string>,
): string[] {
  const out: string[] = [];
  const { cfg } = t;
  const fields: FieldInfo[] = [];
  const fieldByName = new Map<string, FieldInfo>();

  for (const jsKey of [...t.sqlToJsKey.values()]) {
    const col = (t.table as any)[jsKey];
    const field = mapColumn(jsKey, col, enumMap);
    if (field.sqlName === "$hash") {
      field.isGenerated = true;
      field.name = "hash";
    }
    const typedField = applyDerivedFieldOverrides(
      applyTypeDefOverride(field, fieldToTypeDef?.get(`${t.sqlName}.${jsKey}`)),
      t,
    );
    const brand = getFieldBrand(t, typedField.name, branding);
    if (brand) {
      typedField.brand = brand;
    }
    // Polymorphic idField gets a brand array override
    const polyDef = polymorphics?.[t.modelName];
    if (polyDef && typedField.name === polyDef.idField) {
      typedField.brand = polyDef.targets.map((target) => target.tableId);
    }
    fields.push(typedField);
    fieldByName.set(typedField.name, typedField);
  }

  const fkRels = modelRelations.get(t.modelName) || [];
  const fkFieldMap = new Map<string, string[]>();
  for (const rel of fkRels) {
    if (rel.fields) {
      for (const f of rel.fields) {
        if (!fkFieldMap.has(f)) fkFieldMap.set(f, []);
        fkFieldMap.get(f)!.push(rel.fieldName);
      }
    }
  }

  out.push(`        ${t.modelName}: {`);
  out.push(`            name: ${esc(t.modelName)},`);
  out.push(`            fields: {`);

  for (const field of fields) {
    const fkFor = fkFieldMap.get(field.name);
    let block = emitFieldBlock(field, stripRlsSettings);
    if (fkFor) {
      block = block.slice(0, -2) + `, foreignKeyFor: [${fkFor.map((f) => esc(f)).join(", ")}] }`;
    }
    out.push(`                ${field.name}: ${block},`);
  }

  for (const rel of fkRels) {
    // Virtual relations without FK info have no real opposite — plugin handles them
    const isVirtualOnly = (rel.kind === "through") || (rel.kind === "filtered" && !rel.fields);
    const opposite = isVirtualOnly ? "" : (rel._opposite || rel.fieldName);
    const block = emitRelationField(rel, opposite);
    out.push(`                ${rel.fieldName}: ${block},`);
  }

  const invRels = inverseRelations.get(t.modelName) || [];
  for (const rel of invRels) {
    const opposite = rel._opposite || rel.fieldName;
    const block = emitRelationField(rel, opposite);
    out.push(`                ${rel.fieldName}: ${block},`);
  }

  for (const computedField of t.computedFields) {
    out.push(
      `                ${computedField.name}: { name: ${esc(computedField.name)}, type: ${esc(computedField.type)}, computed: true, attributes: [{ name: "@computed" }] },`,
    );
  }

  out.push(`            },`);

  if (t.computedFields.length > 0) {
    out.push(`            computedFields: {`);
    for (const computedField of t.computedFields) {
      out.push(
        `                ${computedField.name}(_context: { modelAlias: ${esc(t.modelName)} }): ${computedField.tsType} { throw new Error("computed stub"); },`,
      );
    }
    out.push(`            },`);
  }

  // Model attributes
  const modelAttrs: string[] = [];
  modelAttrs.push(
    `{ name: "@@map", args: [{ name: "name", value: ExpressionUtils.literal(${esc(t.sqlName)}) }] }`,
  );
  for (const uc of cfg.uniqueConstraints) {
    const ucCols = uc.columns.map((c: any) => c.name);
    if (ucCols.length > 1) {
      modelAttrs.push(
        `{ name: "@@unique", args: [{ name: "fields", value: ExpressionUtils.array("String", [${ucCols.map((c: string) => `ExpressionUtils.field(${esc(t.sqlToJsKey.get(c) || c)})`).join(", ")}]) }] }`,
      );
    }
  }
  if (cfg.primaryKeys.length > 0) {
    for (const pk of cfg.primaryKeys) {
      const pkCols = pk.columns.map((c: any) => c.name);
      modelAttrs.push(
        `{ name: "@@id", args: [{ name: "fields", value: ExpressionUtils.array("String", [${pkCols.map((c: string) => `ExpressionUtils.field(${esc(t.sqlToJsKey.get(c) || c)})`).join(", ")}]) }] }`,
      );
    }
  }
  out.push(`            attributes: [${modelAttrs.join(", ")}],`);

  // idFields
  const idCols = cfg.columns
    .filter((c) => c.primary)
    .map((c) => t.sqlToJsKey.get(c.name) || c.name);
  if (idCols.length === 0 && cfg.primaryKeys.length > 0) {
    const pkCols = cfg.primaryKeys[0]!.columns.map((c: any) => t.sqlToJsKey.get(c.name) || c.name);
    out.push(`            idFields: [${pkCols.map((c: string) => esc(c)).join(", ")}],`);
  } else {
    out.push(`            idFields: [${idCols.map((c) => esc(c)).join(", ")}],`);
  }

  // uniqueFields
  const ufEntries: string[] = [];
  const ufKeys = new Set<string>();

  const addUnique = (cols: string[]) => {
    const key = cols.join("_");
    if (ufKeys.has(key)) return;
    ufKeys.add(key);
    if (cols.length === 1) {
      const field = fieldByName.get(key);
      const parts = [`type: ${esc(field?.type || "String")}`];
      if (field?.brand) {
        parts.push(`brand: ${emitBrandValue(field.brand)}`);
      }
      ufEntries.push(`${key}: { ${parts.join(", ")} }`);
    } else {
      const inner = cols
        .map((c) => {
          const field = fieldByName.get(c);
          const parts = [`type: ${esc(field?.type || "String")}`];
          if (field?.brand) {
            parts.push(`brand: ${emitBrandValue(field.brand)}`);
          }
          return `${c}: { ${parts.join(", ")} }`;
        })
        .join(", ");
      ufEntries.push(`${key}: { ${inner} }`);
    }
  };

  // PK fields
  if (idCols.length > 0) {
    addUnique(idCols);
  } else if (cfg.primaryKeys.length > 0) {
    const pkCols = cfg.primaryKeys[0]!.columns.map((c: any) => t.sqlToJsKey.get(c.name) || c.name);
    addUnique(pkCols);
  }
  // Unique constraints
  for (const uc of cfg.uniqueConstraints) {
    addUnique(uc.columns.map((c: any) => t.sqlToJsKey.get(c.name) || c.name));
  }
  // Single-field unique columns
  for (const f of fields) {
    if (f.unique) addUnique([f.name]);
  }
  out.push(`            uniqueFields: { ${ufEntries.join(", ")} }`);
  out.push(`        },`);

  return out;
}

function emitViewModelBlock(
  v: ViewInfo,
  enumMap: Map<string, EnumInfo>,
  fieldToTypeDef?: Map<string, TypeDefMapping>,
): string[] {
  const out: string[] = [];
  const fields: FieldInfo[] = [];
  const fieldByName = new Map<string, FieldInfo>();

  for (const [jsKey, col] of v.columns) {
    const field = mapColumn(jsKey, col, enumMap);
    if (field.sqlName === "$hash") {
      field.isGenerated = true;
      field.name = "hash";
    }
    const typedField = applyTypeDefOverride(field, fieldToTypeDef?.get(`${v.sqlName}.${jsKey}`));
    fields.push(typedField);
    fieldByName.set(typedField.name, typedField);
  }

  out.push(`        ${v.modelName}: {`);
  out.push(`            name: ${esc(v.modelName)},`);
  out.push(`            isView: true,`);
  out.push(`            fields: {`);

  for (const field of fields) {
    const block = emitFieldBlock(field);
    out.push(`                ${field.name}: ${block},`);
  }

  out.push(`            },`);

  // Model attributes
  const modelAttrs: string[] = [];
  modelAttrs.push(
    `{ name: "@@map", args: [{ name: "name", value: ExpressionUtils.literal(${esc(v.sqlName)}) }] }`,
  );
  out.push(`            attributes: [${modelAttrs.join(", ")}],`);

  // idFields: use primary column or first column
  const idCols: string[] = [];
  for (const [jsKey, col] of v.columns) {
    if ((col as any).primary) {
      const name = jsKey === "$hash" ? "hash" : jsKey;
      idCols.push(name);
    }
  }
  if (idCols.length === 0 && fields.length > 0) {
    idCols.push(fields[0]!.name);
  }
  out.push(`            idFields: [${idCols.map((c) => esc(c)).join(", ")}],`);

  // uniqueFields
  const ufEntries: string[] = [];
  if (idCols.length > 0) {
    const key = idCols.join("_");
    if (idCols.length === 1) {
      ufEntries.push(`${key}: { type: ${esc(fieldByName.get(key)?.type || "String")} }`);
    } else {
      const inner = idCols
        .map((c) => `${c}: { type: ${esc(fieldByName.get(c)?.type || "String")} }`)
        .join(", ");
      ufEntries.push(`${key}: { ${inner} }`);
    }
  }
  // Single-field unique columns
  for (const f of fields) {
    if (f.unique && !idCols.includes(f.name)) {
      const key = f.name;
      const parts = [`type: ${esc(f.type)}`];
      if (f.brand) {
        parts.push(`brand: ${emitBrandValue(f.brand)}`);
      }
      ufEntries.push(`${key}: { ${parts.join(", ")} }`);
    }
  }
  out.push(`            uniqueFields: { ${ufEntries.join(", ")} }`);
  out.push(`        },`);

  return out;
}

function emitRlsBlock(rls: RlsConfig): string {
  const lines: string[] = [];
  lines.push("    rls = {");
  lines.push("        keys: {");
  for (const [name, settingName] of Object.entries(rls.keys)) {
    lines.push(`            ${name}: {`);
    lines.push(`                set: ${esc(settingName)},`);
    lines.push("                resolve: (v: any) => String(v),");
    lines.push("            },");
  }
  lines.push("        },");
  lines.push("        variants: [");
  for (const variant of rls.variants) {
    const parts = Object.entries(variant).map(
      ([k, v]) => `${k}: ${esc(v)}`,
    );
    lines.push(`            { ${parts.join(", ")} },`);
  }
  lines.push("        ],");
  lines.push("    } as const;");
  return lines.join("\n");
}

function emitRlsType(rls: RlsConfig): string {
  const lines: string[] = [];
  lines.push("export type RlsOptions =");
  for (const variant of rls.variants) {
    const parts: string[] = [];
    for (const [key, requirement] of Object.entries(variant)) {
      const optional = requirement === "optional" ? "?" : "";
      parts.push(`${key}${optional}: string`);
    }
    lines.push(`    | { ${parts.join("; ")} }`);
  }
  lines.push(";");
  return lines.join("\n");
}

export function emitSchemaTs({
  tables,
  enumMap,
  modelRelations,
  inverseRelations,
  searchDefaults,
  derivedModels,
  modelScopes,
  typeDefs,
  fieldToTypeDef,
  views,
  derivedFields,
  branding,
  polymorphics,
  rls,
}: EmitSchemaOptions): string {
  const out: string[] = [...emitGeneratedFileBanner()];
  const allEnums = new Map([...enumMap, ...collectDerivedEnums(tables, enumMap)]);
  const hasJsonComputedField = tables.some((table) =>
    table.computedFields.some((field) => field.tsType === "JsonValue"),
  );
  out.push("");
  out.push(
    'import { ExpressionUtils, type FieldDefault, type SchemaDef } from "@zenstackhq/orm/schema";',
  );
  if (hasJsonComputedField) {
    out.push('import type { JsonValue } from "@zenstackhq/orm";');
  }
  out.push("export class SchemaType implements SchemaDef {");
  out.push('    provider = { type: "postgresql" } as const;');

  out.push("    models = {");
  for (const t of tables) {
    out.push(...emitModelBlock(t, enumMap, modelRelations, inverseRelations, fieldToTypeDef, branding, polymorphics));
  }
  if (views) {
    for (const v of views) {
      out.push(...emitViewModelBlock(v, enumMap, fieldToTypeDef));
    }
  }
  out.push("    } as const;");

  // TypeDefs (for typed JSON fields)
  if (typeDefs && typeDefs.size > 0) {
    out.push(emitTypeDefsBlock(typeDefs));
  }

  // Enums
  out.push("    enums = {");
  for (const [, info] of allEnums) {
    out.push(emitEnumBlock(info) + ",");
  }
  out.push("    } as const;");

  out.push(
    emitPluginsBlock(
      tables,
      modelRelations,
      searchDefaults,
      derivedModels ?? {},
      modelScopes ?? {},
      derivedFields ?? {},
    ),
  );
  if (rls) {
    out.push(emitRlsBlock(rls));
  }
  out.push("}");
  out.push("");
  out.push("export const schema = new SchemaType();");
  out.push("");
  if (rls) {
    out.push("");
    out.push(emitRlsType(rls));

    // Emit bypass variant schema — fields with current_setting defaults for non-bypass RLS keys lose their defaults
    const bypassVariant = rls.variants.find((v) => "bypass" in v);
    if (bypassVariant) {
      const bypassKeys = new Set(Object.keys(bypassVariant));
      const strippedSettings = new Set<string>();
      for (const [keyName, settingName] of Object.entries(rls.keys)) {
        if (!bypassKeys.has(keyName)) {
          strippedSettings.add(settingName);
        }
      }

      if (strippedSettings.size > 0) {
        out.push("");
        out.push("export class SchemaType$Bypass implements SchemaDef {");
        out.push('    provider = { type: "postgresql" } as const;');
        out.push("    models = {");
        for (const t of tables) {
          out.push(...emitModelBlock(t, enumMap, modelRelations, inverseRelations, fieldToTypeDef, branding, polymorphics, strippedSettings));
        }
        if (views) {
          for (const v of views) {
            out.push(...emitViewModelBlock(v, enumMap, fieldToTypeDef));
          }
        }
        out.push("    } as const;");
        if (typeDefs && typeDefs.size > 0) {
          out.push(emitTypeDefsBlock(typeDefs));
        }
        out.push("    enums = {");
        for (const [, info] of allEnums) {
          out.push(emitEnumBlock(info) + ",");
        }
        out.push("    } as const;");
        out.push(
          emitPluginsBlock(
            tables,
            modelRelations,
            searchDefaults,
            derivedModels ?? {},
            modelScopes ?? {},
            derivedFields ?? {},
          ),
        );
        out.push(emitRlsBlock(rls));
        out.push("}");
        out.push("");
        out.push("export const schema$Bypass = new SchemaType$Bypass();");
      }
    }
  }

  return out.join("\n");
}

export function emitModelsTs(
  tables: TableInfo[],
  typedJsonFields: TypedJsonFieldInfo[] = [],
  branding?: BrandingInfo,
  derivedFields?: Record<string, Record<string, DerivedFieldConfig>>,
  polymorphics?: Record<string, ResolvedPolymorphic>,
): string {
  const tablesByModel = new Map(tables.map((table) => [table.modelName, table]));
  const jsonFieldAliases = createJsonFieldAliasMap(typedJsonFields);
  const out: string[] = [...emitGeneratedFileBanner()];
  out.push("");
  out.push('import type * as $ from "@zenstackhq/orm";');
  const hasJsonInModels = tables.some((t) =>
    getEntityFieldNames(t).some((f) => jsonFieldAliases.has(`${t.typeName}.${f}`)),
  );
  if (hasJsonInModels) out.push('import type * as Json from "./json";');
  out.push('import { type SchemaType as Schema } from "./schema";');
  out.push("");
  for (const t of tables) {
    const polyDef = polymorphics?.[t.modelName];
    if (polyDef) {
      // Emit per-variant interfaces with narrowed discriminator + idField brands
      const variantNames: string[] = [];
      for (const target of polyDef.targets) {
        const variantName = `${t.typeName}$${toPascalCase(target.table)}`;
        variantNames.push(variantName);
        out.push(
          `interface ${variantName} extends $.DefaultModelResult<Schema, ${esc(t.modelName)}> {`,
        );
        out.push(`  ${polyDef.discriminator}: ${esc(target.table)};`);
        out.push(`  ${polyDef.idField}: $.Brand<${esc(target.tableId)}>;`);
        out.push("}");
      }
      if (polyDef.nullable) {
        const nullVariantName = `${t.typeName}$Null`;
        variantNames.push(nullVariantName);
        out.push(
          `interface ${nullVariantName} extends $.DefaultModelResult<Schema, ${esc(t.modelName)}> {`,
        );
        out.push(`  ${polyDef.discriminator}: null;`);
        out.push(`  ${polyDef.idField}: null;`);
        out.push("}");
      }
      out.push(`export type ${t.typeName} = ${variantNames.join(" | ")};`);
    } else {
      out.push(
        `export interface ${t.typeName} extends $.ModelResult<Schema, ${esc(t.modelName)}> {}`,
      );
    }
    out.push(`export namespace ${t.typeName} {`);
    for (const fieldName of getEntityFieldNames(t)) {
      // Skip polymorphic idField — handled separately below
      if (polyDef && fieldName === polyDef.idField) continue;
      const exportName = toPascalCase(fieldName);
      const jsonAlias = jsonFieldAliases.get(`${t.typeName}.${fieldName}`);
      if (jsonAlias) {
        out.push(`  export interface ${exportName} extends Json.${jsonAlias} {}`);
      } else {
        const brandedLine = branding ? emitBrandedFieldLine(t, fieldName, exportName, branding, "  ") : null;
        if (brandedLine) {
          out.push(...brandedLine);
        } else {
          out.push(`  export type ${exportName} = ${t.typeName}[${esc(fieldName)}];`);
        }
      }
    }
    // Emit polymorphic FK brander (union of all target brands)
    if (polyDef) {
      const idExportName = toPascalCase(polyDef.idField);
      const targetBrands = polyDef.targets.map((target) => `$.Brand<${esc(target.tableId)}>`);
      if (polyDef.nullable) {
        out.push(`  export type ${idExportName} = ${targetBrands.join(" | ")} | null;`);
      } else {
        out.push(`  export type ${idExportName} = ${targetBrands.join(" | ")};`);
      }
      out.push(`  export const ${idExportName} = <T extends string | null | undefined>(value: T): T extends string ? ${idExportName} : T => value as any;`);
    }
    // Emit derived/result-level fields (fullName, initials, etc.)
    const modelDerived = derivedFields
      ? getEffectiveDerivedFields(tablesByModel, derivedFields, t.modelName)
      : {};
    for (const [fieldName] of Object.entries(modelDerived)) {
      const exportName = toPascalCase(fieldName);
      out.push(`  export type ${exportName} = ${t.typeName}[${esc(fieldName)}];`);
    }
    out.push("}");
    out.push("");
  }
  return out.join("\n");
}

/**
 * Emit branded type lines for a single field within a namespace block, or null
 * if the field should use the default (non-branded) emission.
 */
function emitBrandedFieldLine(
  t: TableInfo,
  fieldName: string,
  exportName: string,
  branding: BrandingInfo,
  indent: string,
): string[] | null {
  const customBranded = branding.brandedFields[t.modelName]?.[fieldName];

  // Case 1: UUID primary key field
  if (isUuidField(t, fieldName) && isPkField(t, fieldName)) {
    const brand = `${t.modelName}.${fieldName}`;
    return [
      `${indent}export type ${exportName} = $.Brand<${esc(brand)}>;`,
      `${indent}export const ${exportName} = <T extends string | null | undefined>(value: T): T extends string ? ${exportName} : T => value as any;`,
    ];
  }

  // Case 2: UUID foreign key field — alias to target model's branded ID
  if (isUuidField(t, fieldName)) {
    // Try fkRelations from orm.config first
    let targetExportName = findFkTarget(t, fieldName, branding.fkRelations);
    // Fallback: resolve from Drizzle FK constraints
    if (!targetExportName) {
      targetExportName = findFkTargetFromDrizzle(t, fieldName, branding.allTables);
    }
    if (targetExportName) {
      if (targetExportName === t.exportName) {
        return [`${indent}export type ${exportName} = Id;`];
      }
      const targetTypeName = capitalize(targetExportName);
      return [`${indent}export type ${exportName} = ${targetTypeName}.Id;`];
    }
  }

  // Case 3: Custom branded field (from model config)
  if (customBranded) {
    const brand = `${t.modelName}.${fieldName}`;
    return [
      `${indent}export type ${exportName} = $.Brand<${esc(brand)}>;`,
      `${indent}export const ${exportName} = <T extends string | null | undefined>(value: T): T extends string ? ${exportName} : T => value as any;`,
    ];
  }

  return null;
}

export function emitViewsTs(views: ViewInfo[], typedJsonFields: TypedJsonFieldInfo[] = []): string {
  const jsonFieldAliases = createJsonFieldAliasMap(typedJsonFields);
  const hasJsonAliases = views.some((v) => {
    for (const [jsKey] of v.columns) {
      if (jsonFieldAliases.has(`${v.typeName}.${jsKey}`)) return true;
    }
    return false;
  });
  const out: string[] = [...emitGeneratedFileBanner()];
  out.push("");
  out.push('import type * as $ from "@zenstackhq/orm";');
  if (hasJsonAliases) out.push('import type * as Json from "./json";');
  out.push('import { type SchemaType as Schema } from "./schema";');
  out.push("");
  for (const v of views) {
    out.push(
      `export interface ${v.typeName} extends $.ModelResult<Schema, ${esc(v.modelName)}> {}`,
    );
    out.push(`export namespace ${v.typeName} {`);
    for (const fieldName of getEntityFieldNames(v)) {
      const exportName = toPascalCase(fieldName);
      const jsonAlias = jsonFieldAliases.get(`${v.typeName}.${fieldName}`);
      if (jsonAlias) {
        out.push(`  export interface ${exportName} extends Json.${jsonAlias} {}`);
      } else {
        out.push(`  export type ${exportName} = ${v.typeName}[${esc(fieldName)}];`);
      }
    }
    out.push("}");
    out.push("");
  }
  return out.join("\n");
}

export function emitInputTs(tables: TableInfo[]): string {
  const out: string[] = [...emitGeneratedFileBanner()];
  out.push("");
  out.push('import type * as $ from "@zenstackhq/orm";');
  out.push('import { type SchemaType as Schema } from "./schema";');
  out.push("");
  for (const t of tables) {
    out.push(`export namespace ${t.typeName} {`);
    out.push(
      `  export interface FindManyArgs extends $.FindManyArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(
      `  export interface FindUniqueArgs extends $.FindUniqueArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(
      `  export interface FindFirstArgs extends $.FindFirstArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(`  export interface ExistsArgs extends $.ExistsArgs<Schema, ${esc(t.modelName)}> {}`);
    out.push(
      `  export type CreateData = $.CreateDataInput<Schema, ${esc(t.modelName)}>;`,
    );
    out.push(`  export interface CreateArgs extends $.CreateArgs<Schema, ${esc(t.modelName)}> {}`);
    out.push(
      `  export interface CreateManyArgs extends $.CreateManyArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(
      `  export interface CreateManyAndReturnArgs extends $.CreateManyAndReturnArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(`  export interface UpdateArgs extends $.UpdateArgs<Schema, ${esc(t.modelName)}> {}`);
    out.push(
      `  export interface UpdateManyArgs extends $.UpdateManyArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(
      `  export interface UpdateManyAndReturnArgs extends $.UpdateManyAndReturnArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(`  export interface UpsertArgs extends $.UpsertArgs<Schema, ${esc(t.modelName)}> {}`);
    out.push(`  export interface DeleteArgs extends $.DeleteArgs<Schema, ${esc(t.modelName)}> {}`);
    out.push(
      `  export interface DeleteManyArgs extends $.DeleteManyArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(`  export interface CountArgs extends $.CountArgs<Schema, ${esc(t.modelName)}> {}`);
    out.push(
      `  export interface AggregateArgs extends $.AggregateArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(
      `  export interface GroupByArgs extends $.GroupByArgs<Schema, ${esc(t.modelName)}> {}`,
    );
    out.push(`  export interface WhereInput extends $.WhereInput<Schema, ${esc(t.modelName)}> {}`);
    out.push(`  export interface Select extends $.SelectInput<Schema, ${esc(t.modelName)}> {}`);
    out.push(`  export interface Include extends $.IncludeInput<Schema, ${esc(t.modelName)}> {}`);
    out.push(`  export interface Omit extends $.OmitInput<Schema, ${esc(t.modelName)}> {}`);
    out.push(
      `  export type GetPayload<Args extends $.SelectIncludeOmit<Schema, ${esc(t.modelName)}, true>, Options extends $.QueryOptions<Schema> = $.QueryOptions<Schema>> = $.SimplifiedPlainResult<Schema, ${esc(t.modelName)}, Args, Options>;`,
    );
    out.push("}");
    out.push("");
  }
  return out.join("\n");
}

function fieldToZodSchema(
  field: FieldInfo,
  enumMap: Map<string, EnumInfo>,
  brand?: string | string[],
  isUuid = false,
): string {
  let base: string;

  // Map field type to Zod schema — enum lookup by pgName or zenstackName
  let enumInfo = enumMap.get(field.type);
  if (!enumInfo) {
    for (const e of enumMap.values()) {
      if (e.zenstackName === field.type) { enumInfo = e; break; }
    }
  }
  if (enumInfo) {
    if (enumInfo.mapping) {
      // Use generated enum const via z.nativeEnum()
      base = `z.nativeEnum(E.${enumInfo.zenstackName})`;
    } else {
      base = `z.enum([${enumInfo.values.map(esc).join(", ")}])`;
    }
  } else {
    switch (field.type) {
      case "String":
        base = "z.string()";
        break;
      case "Int":
      case "Float":
      case "Decimal":
        base = "z.number()";
        break;
      case "BigInt":
        base = "z.bigint()";
        break;
      case "Boolean":
        base = "z.boolean()";
        break;
      case "DateTime":
        base = "z.union([z.string(), z.date()])";
        break;
      case "Json":
        base = "z.unknown()";
        break;
      case "Bytes":
        base = "z.instanceof(Uint8Array)";
        break;
      default:
        base = "z.unknown()";
        break;
    }
  }

  // Apply brand via .brand() — output type matches ORM Brand<T> via shared BRAND symbol from zod
  if (brand) {
    if (Array.isArray(brand)) {
      // Polymorphic ID — multiple target brands. Use uuid validation only,
      // brand safety comes from the CreateData type alias.
      base = isUuid ? "z.string().uuid()" : "z.string().min(1)";
    } else {
      const validation = isUuid ? ".uuid()" : ".min(1)";
      base = `z.string()${validation}.brand(${esc(brand)})`;
    }
  }

  // Apply nullable/optional
  if (field.optional) {
    base = `${base}.nullable().optional()`;
  } else if (field.hasDefault || field.isGenerated || field.createdAt || field.updatedAt) {
    base = `${base}.optional()`;
  }

  return base;
}

export function emitValidationTs(
  tables: TableInfo[],
  enumMap: Map<string, EnumInfo>,
  branding?: BrandingInfo,
  polymorphics?: Record<string, ResolvedPolymorphic>,
): string {
  const out: string[] = [...emitGeneratedFileBanner()];
  out.push("");
  out.push('import { z } from "zod";');
  out.push('import type * as $ from "@zenstackhq/orm";');
  out.push('import { type SchemaType as Schema } from "./schema";');
  out.push('import * as E from "./enums";');
  out.push("");
  for (const t of tables) {
    const polyDef = polymorphics?.[t.modelName];

    // Collect fields eligible for create (scalar + FK, no relations)
    const fieldEntries: string[] = [];
    for (const jsKey of [...t.sqlToJsKey.values()]) {
      const col = (t.table as any)[jsKey];
      const field = mapColumn(jsKey, col, enumMap);
      if (field.isGenerated && field.sqlName === "$hash") continue;

      // Skip relation fields (they have no column)
      const fieldDef = (t.table as any)[jsKey];
      if (!fieldDef || typeof fieldDef !== "object" || !("columnType" in fieldDef)) continue;

      // Resolve brand
      let brand: string | string[] | undefined;
      if (polyDef && jsKey === polyDef.idField) {
        brand = polyDef.targets.map((target) => target.tableId);
      } else if (branding) {
        const b = getFieldBrand(t, jsKey, branding);
        if (b) brand = b;
      }

      fieldEntries.push(`    ${jsKey}: ${fieldToZodSchema(field, enumMap, brand, isUuidField(t, jsKey))}`);
    }

    out.push(`export namespace ${t.typeName} {`);
    out.push(`  export type CreateData = $.CreateDataInput<Schema, ${esc(t.modelName)}>;`);

    if (polyDef) {
      // Polymorphic model: emit base schema + builder + default discriminated union
      const sharedEntries = fieldEntries.filter(
        (e) => !e.trimStart().startsWith(`${polyDef.discriminator}:`) &&
               !e.trimStart().startsWith(`${polyDef.idField}:`),
      );
      const isUuid = isUuidField(t, polyDef.idField);
      const validation = isUuid ? ".uuid()" : ".min(1)";

      out.push(`  export const CreateDataBaseSchema = z.object({`);
      out.push(sharedEntries.join(",\n") + ",");
      out.push(`  });`);

      // Builder function: customize base, get discriminated union back
      out.push(`  export function buildCreateDataSchema<T extends z.ZodRawShape>(`);
      out.push(`    customize?: (base: typeof CreateDataBaseSchema) => z.ZodObject<T>,`);
      out.push(`  ) {`);
      out.push(`    const shape = customize ? customize(CreateDataBaseSchema).shape : CreateDataBaseSchema.shape;`);
      out.push(`    return z.discriminatedUnion(${esc(polyDef.discriminator)}, [`);
      for (const target of polyDef.targets) {
        out.push(`      z.object({ ...shape, ${polyDef.discriminator}: z.literal(${esc(target.table)}), ${polyDef.idField}: z.string()${validation}.brand(${esc(target.tableId)}) }),`);
      }
      if (polyDef.nullable) {
        out.push(`      z.object({ ...shape, ${polyDef.discriminator}: z.literal(null), ${polyDef.idField}: z.null() }),`);
      }
      out.push(`    ]);`);
      out.push(`  }`);

      // Default export — full schema with all fields
      out.push(`  export const CreateDataSchema = buildCreateDataSchema();`);
    } else {
      out.push(`  export const CreateDataSchema = z.object({`);
      out.push(fieldEntries.join(",\n") + ",");
      out.push(`  });`);
    }

    out.push("}");
    out.push("");
  }
  return out.join("\n");
}

export function emitIndexTs({
  tables,
  views,
  enumMap,
  typeDefs,
  typedJsonFields,
  branding,
  polymorphics,
}: {
  tables: TableInfo[];
  views: ViewInfo[];
  enumMap: Map<string, EnumInfo>;
  typeDefs: Map<string, TypeDefInfo>;
  typedJsonFields: TypedJsonFieldInfo[];
  branding?: BrandingInfo;
  polymorphics?: Record<string, ResolvedPolymorphic>;
}): string {
  const allEnums = new Map([...enumMap, ...collectDerivedEnums(tables, enumMap)]);
  const jsonFieldAliases = createJsonFieldAliasMap(typedJsonFields);
  const out: string[] = [...emitGeneratedFileBanner()];
  out.push("");
  out.push('import type * as $ from "@zenstackhq/orm";');
  const hasRawJson = typedJsonFields.some((f) => f.sourceTypeName);
  if (hasRawJson) out.push('import type * as RawJson from "../../supabase/schema/types";');
  out.push('import { type SchemaType as Schema } from "./schema";');
  out.push("");
  out.push("export namespace Json {");
  out.push(...emitJsonNamespaceBlock(typeDefs, typedJsonFields, "  "));
  out.push("}");
  out.push("");
  out.push("export namespace JsonRaw {");
  const jsonRawLines = emitJsonRawNamespaceBlock(typedJsonFields, "  ");
  if (jsonRawLines.length > 0) {
    out.push(...jsonRawLines);
  }
  out.push("}");
  out.push("");
  out.push("export namespace Table {");
  for (const table of tables) {
    out.push(...emitEntityNamespaceBlock(table, table.modelName, jsonFieldAliases, "  ", branding, polymorphics?.[table.modelName]));
    out.push("");
  }
  out.push("}");
  out.push("");
  out.push("export namespace View {");
  for (const view of views) {
    out.push(...emitEntityNamespaceBlock(view, view.modelName, jsonFieldAliases, "  "));
    out.push("");
  }
  out.push("}");
  out.push("");
  out.push("export namespace Input {");
  for (const table of tables) {
    out.push(...emitInputNamespaceBlock(table, "  "));
    out.push("");
  }
  out.push("}");
  out.push("");
  out.push("export namespace ComputedFields {");
  out.push("  export type All = $.ComputedFieldsOptions<Schema>;");
  for (const table of tables) {
    if (table.computedFields.length === 0) continue;
    out.push(
      `  export type ${table.typeName} = $.ComputedFieldsOptions<Schema>[${esc(table.modelName)}];`,
    );
  }
  out.push("}");
  out.push("");
  out.push("export namespace Enum {");
  for (const [, info] of allEnums) {
    out.push(...emitEnumNamespaceBlock(info, "  "));
    out.push("");
  }
  out.push("}");
  out.push("");
  out.push('export * as Validation from "./validation";');
  out.push("");
  return out.join("\n");
}

export function emitCompatTs({
  tables,
  views,
  enumMap,
  typedJsonFields,
}: {
  tables: TableInfo[];
  views: ViewInfo[];
  enumMap: Map<string, EnumInfo>;
  typedJsonFields: TypedJsonFieldInfo[];
}): string {
  const out: string[] = [...emitGeneratedFileBanner()];
  const jsonAliases = typedJsonFields.map(
    (field) => `${field.entityTypeName}${toPascalCase(field.fieldName)}Json`,
  );
  const jsonRawAliases = typedJsonFields
    .filter((field) => field.sourceTypeName)
    .map((field) => `${field.entityTypeName}${toPascalCase(field.fieldName)}JsonRaw`);

  out.push("");
  out.push('import type * as $ from "@zenstackhq/orm";');
  out.push('import * as Db from "./index";');
  out.push('import type { SchemaType as Schema } from "./schema";');
  out.push("");

  out.push("export namespace Models {");
  for (const table of tables) {
    out.push(...emitCompatEntityAlias(table, "Table", "  "));
    out.push("");
  }
  out.push("}");
  out.push("");

  out.push("export namespace Views {");
  for (const view of views) {
    out.push(...emitCompatEntityAlias(view, "View", "  "));
    out.push("");
  }
  out.push("}");
  out.push("");

  out.push("export namespace Input {");
  for (const table of tables) {
    out.push(...emitCompatInputAliases(table, "  "));
    out.push("");
  }
  out.push("}");
  out.push("");

  out.push("export namespace Json {");
  for (const alias of jsonAliases) {
    out.push(...emitDeprecatedTypeAlias(alias, `Db.Json.${alias}`, "  "));
  }
  out.push("}");
  out.push("");

  out.push("export namespace JsonRaw {");
  for (const alias of jsonRawAliases) {
    out.push(...emitDeprecatedTypeAlias(alias, `Db.JsonRaw.${alias}`, "  "));
  }
  out.push("}");
  out.push("");

  out.push("export namespace Enums {");
  for (const [, info] of enumMap) {
    if (!info.mapping) continue;
    out.push(...emitDeprecatedEnumAliases(info.zenstackName, "  "));
    out.push("");
  }
  out.push("}");
  out.push("");

  for (const table of tables) {
    out.push(...emitDeprecatedTypeAlias(table.typeName, `Models.${table.typeName}`));
  }
  for (const view of views) {
    out.push(...emitDeprecatedTypeAlias(view.typeName, `Views.${view.typeName}`));
  }
  for (const table of tables) {
    out.push(...emitCompatInputAliases(table));
  }
  for (const alias of jsonAliases) {
    out.push(...emitDeprecatedTypeAlias(alias, `Json.${alias}`));
  }
  for (const alias of jsonRawAliases) {
    out.push(...emitDeprecatedTypeAlias(alias, `JsonRaw.${alias}`));
  }
  for (const [, info] of enumMap) {
    if (!info.mapping) continue;
    out.push(...emitDeprecatedEnumAliases(info.zenstackName));
    out.push("");
  }

  return out.join("\n");
}

export function emitOrmTypesTs(tables: TableInfo[]): string {
  const out: string[] = [...emitGeneratedFileBanner()];
  out.push("");
  out.push('import type * as $ from "@zenstackhq/orm";');
  out.push('import type { SchemaType as Schema } from "./schema";');
  out.push("");
  out.push("export type Client = $.ClientContract<Schema>;");
  out.push("");
  out.push("export namespace ComputedFields {");
  out.push("  export type All = $.ComputedFieldsOptions<Schema>;");
  for (const table of tables) {
    if (table.computedFields.length === 0) continue;
    out.push(
      `  export type ${table.typeName} = $.ComputedFieldsOptions<Schema>[${esc(table.modelName)}];`,
    );
  }
  out.push("}");
  out.push("");
  return out.join("\n");
}

function getEntityFieldNames(entity: TableInfo | ViewInfo): string[] {
  const fieldNames = new Set<string>();

  if ("table" in entity) {
    for (const jsKey of entity.sqlToJsKey.values()) {
      fieldNames.add(jsKey === "$hash" ? "hash" : jsKey);
    }
    for (const computedField of entity.computedFields) {
      fieldNames.add(computedField.name);
    }
  } else {
    for (const jsKey of entity.columns.keys()) {
      fieldNames.add(jsKey === "$hash" ? "hash" : jsKey);
    }
  }

  return [...fieldNames];
}

function createJsonFieldAliasMap(typedJsonFields: TypedJsonFieldInfo[]): Map<string, string> {
  return new Map(
    typedJsonFields.map((field) => [
      `${field.entityTypeName}.${field.fieldName}`,
      `${field.entityTypeName}${toPascalCase(field.fieldName)}Json`,
    ]),
  );
}

function emitEntityNamespaceBlock(
  entity: TableInfo | ViewInfo,
  modelName: string,
  jsonFieldAliases: Map<string, string>,
  indent = "",
  branding?: BrandingInfo,
  polyDef?: ResolvedPolymorphic,
): string[] {
  const out: string[] = [];
  if (polyDef) {
    const variantNames: string[] = [];
    for (const target of polyDef.targets) {
      const variantName = `${entity.typeName}$${toPascalCase(target.table)}`;
      variantNames.push(variantName);
      out.push(
        `${indent}interface ${variantName} extends $.DefaultModelResult<Schema, ${esc(modelName)}> {`,
      );
      out.push(`${indent}  ${polyDef.discriminator}: ${esc(target.table)};`);
      out.push(`${indent}  ${polyDef.idField}: $.Brand<${esc(target.tableId)}>;`);
      out.push(`${indent}}`);
    }
    if (polyDef.nullable) {
      const nullVariantName = `${entity.typeName}$Null`;
      variantNames.push(nullVariantName);
      out.push(
        `${indent}interface ${nullVariantName} extends $.DefaultModelResult<Schema, ${esc(modelName)}> {`,
      );
      out.push(`${indent}  ${polyDef.discriminator}: null;`);
      out.push(`${indent}  ${polyDef.idField}: null;`);
      out.push(`${indent}}`);
    }
    out.push(`${indent}export type ${entity.typeName} = ${variantNames.join(" | ")};`);
  } else {
    out.push(
      `${indent}export interface ${entity.typeName} extends $.ModelResult<Schema, ${esc(modelName)}> {}`,
    );
  }
  out.push(`${indent}export namespace ${entity.typeName} {`);
  for (const fieldName of getEntityFieldNames(entity)) {
    if (polyDef && fieldName === polyDef.idField) continue;
    const exportName = toPascalCase(fieldName);
    const jsonAlias = jsonFieldAliases.get(`${entity.typeName}.${fieldName}`);
    if (jsonAlias) {
      out.push(`${indent}  export interface ${exportName} extends Json.${jsonAlias} {}`);
    } else {
      const isTable = "table" in entity;
      const brandedLine = isTable && branding
        ? emitBrandedFieldLine(entity as TableInfo, fieldName, exportName, branding, `${indent}  `)
        : null;
      if (brandedLine) {
        out.push(...brandedLine);
      } else {
        out.push(`${indent}  export type ${exportName} = ${entity.typeName}[${esc(fieldName)}];`);
      }
    }
  }
  if (polyDef) {
    const idExportName = toPascalCase(polyDef.idField);
    const targetBrands = polyDef.targets.map((target) => `$.Brand<${esc(target.tableId)}>`);
    if (polyDef.nullable) {
      out.push(`${indent}  export type ${idExportName} = ${targetBrands.join(" | ")} | null;`);
    } else {
      out.push(`${indent}  export type ${idExportName} = ${targetBrands.join(" | ")};`);
    }
    out.push(`${indent}  export const ${idExportName} = <T extends string | null | undefined>(value: T): T extends string ? ${idExportName} : T => value as any;`);
  }
  out.push(`${indent}}`);
  return out;
}

function emitInputNamespaceBlock(table: TableInfo, indent = ""): string[] {
  const out: string[] = [];
  out.push(`${indent}export namespace ${table.typeName} {`);
  out.push(
    `${indent}  export interface FindManyArgs extends $.FindManyArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface FindUniqueArgs extends $.FindUniqueArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface FindFirstArgs extends $.FindFirstArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface ExistsArgs extends $.ExistsArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export type CreateData = $.CreateDataInput<Schema, ${esc(table.modelName)}>;`,
  );
  out.push(
    `${indent}  export interface CreateArgs extends $.CreateArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface CreateManyArgs extends $.CreateManyArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface CreateManyAndReturnArgs extends $.CreateManyAndReturnArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface UpdateArgs extends $.UpdateArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface UpdateManyArgs extends $.UpdateManyArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface UpdateManyAndReturnArgs extends $.UpdateManyAndReturnArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface UpsertArgs extends $.UpsertArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface DeleteArgs extends $.DeleteArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface DeleteManyArgs extends $.DeleteManyArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface CountArgs extends $.CountArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface AggregateArgs extends $.AggregateArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface GroupByArgs extends $.GroupByArgs<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface WhereInput extends $.WhereInput<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface Select extends $.SelectInput<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface Include extends $.IncludeInput<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export interface Omit extends $.OmitInput<Schema, ${esc(table.modelName)}> {}`,
  );
  out.push(
    `${indent}  export type GetPayload<Args extends $.SelectIncludeOmit<Schema, ${esc(table.modelName)}, true>, Options extends $.QueryOptions<Schema> = $.QueryOptions<Schema>> = $.SimplifiedPlainResult<Schema, ${esc(table.modelName)}, Args, Options>;`,
  );
  out.push(`${indent}}`);
  return out;
}

function emitEnumNamespaceBlock(info: EnumInfo, indent = ""): string[] {
  if (!info.mapping) return [];

  const out: string[] = [];
  const entries = Object.entries(info.mapping)
    .map(([key, value]) => `${indent}  ${key}: ${esc(value)}`)
    .join(",\n");
  out.push(`${indent}export const ${info.zenstackName} = {`);
  out.push(entries);
  out.push(`${indent}} as const;`);
  out.push(
    `${indent}export type ${info.zenstackName} = (typeof ${info.zenstackName})[keyof typeof ${info.zenstackName}];`,
  );
  out.push(
    `${indent}export type ${info.zenstackName}Key = ${Object.keys(info.mapping)
      .map((key) => esc(key))
      .join(" | ")};`,
  );
  out.push(
    `${indent}export const ${info.zenstackName}Values = Object.values(${info.zenstackName});`,
  );
  out.push(
    `${indent}export const ${info.zenstackName}Keys = Object.keys(${info.zenstackName}) as ${info.zenstackName}Key[];`,
  );
  return out;
}

function emitDeprecatedTypeAlias(name: string, target: string, indent = ""): string[] {
  return [
    `${indent}/** @deprecated Use ${target} instead. */`,
    `${indent}export type ${name} = ${target};`,
  ];
}

function emitDeprecatedValueAlias(name: string, target: string, indent = ""): string[] {
  return [
    `${indent}/** @deprecated Use ${target} instead. */`,
    `${indent}export const ${name} = ${target};`,
  ];
}

function emitDeprecatedEnumAliases(enumName: string, indent = ""): string[] {
  return [
    ...emitDeprecatedValueAlias(enumName, `Db.Enum.${enumName}`, indent),
    ...emitDeprecatedTypeAlias(enumName, `Db.Enum.${enumName}`, indent),
    ...emitDeprecatedTypeAlias(`${enumName}Key`, `Db.Enum.${enumName}Key`, indent),
    ...emitDeprecatedValueAlias(`${enumName}Values`, `Db.Enum.${enumName}Values`, indent),
    ...emitDeprecatedValueAlias(`${enumName}Keys`, `Db.Enum.${enumName}Keys`, indent),
  ];
}

function emitCompatEntityAlias(
  entity: TableInfo | ViewInfo,
  namespace: "Table" | "View",
  indent = "",
): string[] {
  const target = `Db.${namespace}.${entity.typeName}`;
  const out: string[] = [`${indent}/** @deprecated Use ${target} instead. */`];
  const legacyFields = [...entity.sqlToJsKey.entries()]
    .map(([sqlName, jsName]) => ({
      sqlName,
      fieldName: jsName === "$hash" ? "hash" : jsName,
    }))
    .filter(({ sqlName, fieldName }) => sqlName !== "$hash" && sqlName !== fieldName);

  if (legacyFields.length === 0) {
    out.push(`${indent}export type ${entity.typeName} = ${target};`);
    return out;
  }

  out.push(`${indent}export type ${entity.typeName} = ${target} & {`);
  for (const { sqlName, fieldName } of legacyFields) {
    out.push(`${indent}  /** @deprecated Use ${fieldName} instead. */`);
    out.push(`${indent}  ${esc(sqlName)}: ${target}[${esc(fieldName)}];`);
  }
  out.push(`${indent}};`);
  return out;
}

function emitCompatInputAliases(table: TableInfo, indent = ""): string[] {
  const target = `Db.Input.${table.typeName}`;

  return [
    ...emitDeprecatedTypeAlias(`${table.typeName}FindManyArgs`, `${target}.FindManyArgs`, indent),
    ...emitDeprecatedTypeAlias(
      `${table.typeName}FindUniqueArgs`,
      `${target}.FindUniqueArgs`,
      indent,
    ),
    ...emitDeprecatedTypeAlias(`${table.typeName}FindFirstArgs`, `${target}.FindFirstArgs`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}ExistsArgs`, `${target}.ExistsArgs`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}CreateArgs`, `${target}.CreateArgs`, indent),
    ...emitDeprecatedTypeAlias(
      `${table.typeName}CreateManyArgs`,
      `${target}.CreateManyArgs`,
      indent,
    ),
    ...emitDeprecatedTypeAlias(
      `${table.typeName}CreateManyAndReturnArgs`,
      `${target}.CreateManyAndReturnArgs`,
      indent,
    ),
    ...emitDeprecatedTypeAlias(`${table.typeName}UpdateArgs`, `${target}.UpdateArgs`, indent),
    ...emitDeprecatedTypeAlias(
      `${table.typeName}UpdateManyArgs`,
      `${target}.UpdateManyArgs`,
      indent,
    ),
    ...emitDeprecatedTypeAlias(
      `${table.typeName}UpdateManyAndReturnArgs`,
      `${target}.UpdateManyAndReturnArgs`,
      indent,
    ),
    ...emitDeprecatedTypeAlias(`${table.typeName}UpsertArgs`, `${target}.UpsertArgs`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}DeleteArgs`, `${target}.DeleteArgs`, indent),
    ...emitDeprecatedTypeAlias(
      `${table.typeName}DeleteManyArgs`,
      `${target}.DeleteManyArgs`,
      indent,
    ),
    ...emitDeprecatedTypeAlias(`${table.typeName}CountArgs`, `${target}.CountArgs`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}AggregateArgs`, `${target}.AggregateArgs`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}GroupByArgs`, `${target}.GroupByArgs`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}WhereInput`, `${target}.WhereInput`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}Select`, `${target}.Select`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}Include`, `${target}.Include`, indent),
    ...emitDeprecatedTypeAlias(`${table.typeName}Omit`, `${target}.Omit`, indent),
    `${indent}/** @deprecated Use ${target}.GetPayload instead. */`,
    `${indent}export type ${table.typeName}GetPayload<Args extends $.SelectIncludeOmit<Schema, ${esc(table.modelName)}, true>, Options extends $.QueryOptions<Schema> = $.QueryOptions<Schema>> = ${target}.GetPayload<Args, Options>;`,
  ];
}

// ─── enums/ ─────────────────────────────────────────────────────────────

export function emitEnumsTs(enumMap: Map<string, EnumInfo>): string {
  const out: string[] = [...emitGeneratedFileBanner()];
  out.push("");
  for (const [, info] of enumMap) {
    if (!info.mapping) continue;
    const n = info.zenstackName;
    const entries = Object.entries(info.mapping)
      .map(([k, v]) => `  ${k}: ${esc(v)}`)
      .join(",\n");
    out.push(`export const ${n} = {\n${entries},\n} as const;`);
    out.push(`export type ${n} = (typeof ${n})[keyof typeof ${n}];`);
    out.push(
      `export type ${n}Key = ${Object.keys(info.mapping)
        .map((k) => esc(k))
        .join(" | ")};`,
    );
    out.push(`export const ${n}Values = Object.values(${n});`);
    out.push(`export const ${n}Keys = Object.keys(${n}) as ${n}Key[];`);
    out.push("");
  }
  return out.join("\n");
}
